import React, { useState } from 'react';
import { useClient } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { PlusCircle, Pencil, Trash2, AlertTriangle, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { ClientAccessSection } from '../components/ClientAccessSection';
import { ClientCashCloseSection } from '../components/ClientCashCloseSection';

const TYPES = ['Fine Dining', 'Casual Dining', 'QSR', 'Café', 'Cloud Kitchen', 'Hotel F&B'];
const THUMBS = [
  'https://images.pexels.com/photos/10445929/pexels-photo-10445929.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://images.pexels.com/photos/32399761/pexels-photo-32399761.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
];

const EMPTY = { name: '', restaurant_type: '', location: '', thumbnail_url: '', owner_email: '' };

export const ClientManagement = () => {
  const { clients, API, addClient, updateClient, deleteClient, setSelectedClient } = useClient();
  const { user } = useAuth();
  // Both 'operator' (new) and legacy 'admin' unlock admin actions.
  const isAdmin = user?.role === 'operator' || user?.role === 'admin';

  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // client object pending delete
  const [delResult, setDelResult] = useState(null);
  const [error, setError] = useState(null);

  const startAdd = () => { setEditingId(null); setForm(EMPTY); setError(null); setAddOpen(true); };
  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || '',
      restaurant_type: c.restaurant_type || '',
      location: c.location || '',
      thumbnail_url: c.thumbnail_url || '',
    });
    setError(null);
    setAddOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (editingId) {
        await updateClient(editingId, form);
      } else {
        const newClient = await addClient(form);
        setSelectedClient(newClient);
      }
      setAddOpen(false);
      setForm(EMPTY);
      setEditingId(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  const doDelete = async (cascade) => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      const res = await deleteClient(confirmDel.id, cascade);
      setDelResult(res);
      setConfirmDel(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="client-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-primary tracking-tight">Client Management</h2>
          <p className="text-text-secondary mt-1">
            Manage your restaurant clients{isAdmin ? ' · admin actions enabled' : ''}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={startAdd} className="bg-primary hover:bg-primary-hover" data-testid="add-client-button">
            <PlusCircle className="mr-2 h-4 w-4" />
            Add New Client
          </Button>
        )}
      </div>

      {delResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs flex items-start gap-2" data-testid="delete-result">
          <div className="flex-1">
            <strong>{delResult.client_name}</strong> deleted.
            {delResult.cascade && Object.keys(delResult.removed || {}).length > 0 && (
              <span className="ml-2 text-text-secondary">
                Removed{' '}
                {Object.entries(delResult.removed).map(([k, n]) => `${n} ${k}`).join(', ')}
              </span>
            )}
          </div>
          <button onClick={() => setDelResult(null)} className="text-text-muted hover:text-text-primary"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {clients.map((client) => (
          <div
            key={client.id}
            className="bg-white border border-border rounded-md overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all duration-200 group relative"
            data-testid={`client-card-${client.id}`}
          >
            {isAdmin && (
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); startEdit(client); }}
                  className="bg-white/95 backdrop-blur-sm border border-border rounded-md p-1.5 text-text-secondary hover:text-primary hover:border-primary shadow-sm"
                  data-testid={`edit-client-${client.id}`}
                  title="Edit client"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setConfirmDel(client); }}
                  className="bg-white/95 backdrop-blur-sm border border-border rounded-md p-1.5 text-state-error hover:bg-red-50 hover:border-state-error shadow-sm"
                  data-testid={`delete-client-${client.id}`}
                  title="Delete client"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <div onClick={() => setSelectedClient(client)} className="cursor-pointer">
              {client.thumbnail_url && (
                <img
                  src={client.thumbnail_url}
                  alt={client.name}
                  className="w-full h-40 object-cover"
                />
              )}
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-primary">{client.name}</h3>
                  <div className="h-2 w-2 rounded-full bg-state-success"></div>
                </div>
                <p className="text-sm text-text-secondary">{client.restaurant_type}</p>
                <p className="text-sm text-text-muted mt-1">{client.location}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Client' : 'Add New Client'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Restaurant Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20"
                required
                data-testid="client-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <select
                value={form.restaurant_type}
                onChange={(e) => setForm({ ...form, restaurant_type: e.target.value })}
                className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20"
                required
                data-testid="client-type-select"
              >
                <option value="">Select type</option>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Location</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20"
                required
                data-testid="client-location-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Thumbnail (optional)</label>
              <select
                value={form.thumbnail_url}
                onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })}
                className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20"
                data-testid="client-thumbnail-select"
              >
                <option value="">No thumbnail</option>
                {THUMBS.map((url, idx) => (
                  <option key={idx} value={url}>Thumbnail {idx + 1}</option>
                ))}
              </select>
            </div>
            {/* Owner-email — atomic bind at creation. Only shown on Add. */}
            {!editingId && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Owner email <span className="text-text-muted font-normal">(optional · binds access at creation)</span>
                </label>
                <input
                  type="email"
                  value={form.owner_email}
                  onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                  className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20"
                  placeholder="owner@yourrestaurant.com"
                  data-testid="client-owner-email-input"
                />
                <p className="text-xs text-text-muted mt-1">
                  Leave empty to create the client with no access. You can add emails later from the Edit view.
                </p>
              </div>
            )}
            {error && <div className="text-xs text-state-error bg-red-50 border border-red-200 rounded p-2">{error}</div>}

            {/* Onboarding — required-if-available data sources.
                Only shown on Add (not Edit). One-line explanation for each. */}
            {!editingId && (
              <div
                data-testid="onboarding-checklist"
                className="text-xs bg-amber-50 border border-amber-200 rounded p-3 space-y-2"
              >
                <div className="font-semibold text-amber-900">After creation — upload the following via Files</div>
                <ul className="space-y-1.5 text-amber-900/85">
                  <li data-testid="onboarding-step-settlement">
                    <strong>Platform settlement reports</strong>
                    <span className="text-amber-800/70"> · required if available</span>
                    <div className="text-[11px] text-amber-800/75">
                      Zomato and Swiggy CSVs. Unlocks order-level cause attribution — without them the analysis is aggregate variance only.
                    </div>
                  </li>
                  <li>
                    <strong>Bank statement</strong>
                    <span className="text-amber-800/70"> · required</span>
                    <div className="text-[11px] text-amber-800/75">Closes the loop: settlement UTR → bank credit → reconciled.</div>
                  </li>
                  <li>
                    <strong>POS export</strong>
                    <span className="text-amber-800/70"> · required</span>
                    <div className="text-[11px] text-amber-800/75">Bill-wise or Z-report. Establishes what was owed.</div>
                  </li>
                </ul>
              </div>
            )}

            <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary-hover" data-testid="submit-client-button">
              {busy ? 'Saving…' : (editingId ? 'Save Changes' : 'Add Client')}
            </Button>
          </form>

          {/* Access section — only on Edit (needs a persisted client id). */}
          {editingId && (
            <>
              <ClientAccessSection
                clientId={editingId}
                clientName={form.name}
                API={API}
              />
              <ClientCashCloseSection
                clientId={editingId}
                clientName={form.name}
                API={API}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-state-error" />
              Delete <span className="text-state-error">{confirmDel?.name}</span>?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-text-secondary">
              This will permanently delete this client and <strong>all associated data</strong>:
              uploaded files, expenses, menu items, sales, suppliers, recipes, competitors, reviews,
              AI extractions, reconciliations, and reports.
            </p>
            <p className="text-state-error font-semibold">This cannot be undone.</p>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => setConfirmDel(null)} disabled={busy}>Cancel</Button>
            <Button
              onClick={() => doDelete(true)}
              disabled={busy}
              className="bg-state-error hover:bg-red-700 text-white"
              data-testid="confirm-delete-client"
            >
              {busy ? 'Deleting…' : `Yes, delete ${confirmDel?.name}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
